import React, {useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {createBrand} from 'api/registries/gtins';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Checkbox} from 'components/ui-kit/Checkbox';
import {Input} from 'components/ui-kit/Input';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {showRestError} from 'core/errors';
import {profile$} from 'models/auth';
import {BrandCreateParams} from 'types/gtins';

import {
    BackButton,
    BrandOwnerCheckbox,
    Container,
    FieldLabel,
    Fields,
    Footer,
    RequiredMark,
    Title,
    WarningBox,
    WarningTitle,
} from './styled';

type Country = {
    country: string;
    countryFr: string;
    code: string;
};

type CreateBrandContentProps = {
    countries: Country[];
    initialBrandName: string;
    onBack: () => void;
    onCreated: (brandName: string) => void;
};

export const CreateBrandContent = ({
    countries,
    initialBrandName,
    onBack,
    onCreated,
}: CreateBrandContentProps) => {
    const {t, i18n} = useTranslation();
    const profile = useStore(profile$);
    const [isPending, setIsPending] = useState(false);
    const [isBrandOwner, setIsBrandOwner] = useState(false);

    const schema = useMemo(
        () =>
            yup.object().shape({
                brandName: yup
                    .string()
                    .trim()
                    .required(t('components.form.required')),
                legalOwnerName: yup
                    .string()
                    .trim()
                    .required(t('components.form.required')),
                country: yup
                    .string()
                    .trim()
                    .required(t('components.form.required')),
            }),
        [t],
    );

    const countryOptions = useMemo(
        () =>
            countries.map((item) => ({
                label: (i18n.language === 'en' ? item.country : item.countryFr) ?? item.country,
                value: item.code,
            })),
        [countries, i18n.language],
    );

    const profileBrandOwnerValues = useMemo(() => {
        const legalOwnerName =
            profile?.legalPerson?.company_name ||
            profile?.client?.legalName ||
            profile?.client?.fullName ||
            '';
        const profileCountry = profile?.legalPerson?.country ?? '';
        const normalizedProfileCountry = profileCountry.toLowerCase();
        const country =
            countries.find(
                (item) =>
                    item.code.toLowerCase() === normalizedProfileCountry ||
                    item.country.toLowerCase() === normalizedProfileCountry ||
                    item.countryFr.toLowerCase() === normalizedProfileCountry,
            )?.code ?? profileCountry;

        return {
            legalOwnerName,
            country,
        };
    }, [countries, profile]);

    const {
        control,
        formState: {errors, isValid},
        handleSubmit,
        reset,
        setValue,
        trigger,
    } = useForm<BrandCreateParams>({
        resolver: yupResolver(schema),
        defaultValues: {
            brandName: initialBrandName,
            legalOwnerName: '',
            country: '',
        },
        mode: 'all',
    });

    useEffect(() => {
        reset({
            brandName: initialBrandName,
            legalOwnerName: '',
            country: '',
        });
        setIsBrandOwner(false);
    }, [initialBrandName, reset]);

    useEffect(() => {
        if (!isBrandOwner) return;

        setValue('legalOwnerName', profileBrandOwnerValues.legalOwnerName, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue('country', profileBrandOwnerValues.country, {
            shouldDirty: true,
            shouldValidate: true,
        });
        trigger(['legalOwnerName', 'country']);
    }, [isBrandOwner, profileBrandOwnerValues, setValue, trigger]);

    const onSubmit = handleSubmit(async (data) => {
        try {
            setIsPending(true);
            const response = await createBrand(data);
            toast.success(t('pages.registries.createBrand.success'));
            onCreated(response?.brandName ?? '');
        } catch (error) {
            showRestError(error);
            console.error(error);
        } finally {
            setIsPending(false);
        }
    });

    return (
        <Container>
            <BackButton type="button" onClick={onBack}>
                {t('pages.registries.createBrand.back')}
            </BackButton>
            <Title>{t('pages.registries.createBrand.title')}</Title>
            <Fields>
                <div>
                    <FieldLabel>
                        {t('pages.registries.createBrand.brandName')} <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <Controller
                        name="brandName"
                        control={control}
                        render={({value, onChange, ...rest}) => (
                            <Input
                                {...rest}
                                value={value}
                                onChange={onChange}
                                hasError={!!errors.brandName}
                                placeholder={t('pages.registries.createBrand.brandNamePlaceholder')}
                            />
                        )}
                    />
                </div>
                <div>
                    <FieldLabel>
                        {t('pages.registries.createBrand.legalOwnerName')}{' '}
                        <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <Controller
                        name="legalOwnerName"
                        control={control}
                        render={({value, onChange, ...rest}) => (
                            <Input
                                {...rest}
                                value={value}
                                onChange={onChange}
                                disabled={isBrandOwner}
                                hasError={!!errors.legalOwnerName}
                                placeholder={t(
                                    'pages.registries.createBrand.legalOwnerNamePlaceholder',
                                )}
                            />
                        )}
                    />
                </div>
                <div>
                    <FieldLabel>
                        {t('pages.orders.form.country')} <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <Controller
                        name="country"
                        control={control}
                        render={({value, onChange, onBlur}) => (
                            <Select
                                value={
                                    countryOptions.find((option) => option.value === value) ?? null
                                }
                                options={countryOptions}
                                onBlur={onBlur}
                                onChange={(option: SelectOption<string>) => onChange(option.value)}
                                disabled={isBrandOwner}
                                hasError={!!errors.country}
                                placeholder={t('pages.registries.createBrand.countryPlaceholder')}
                            />
                        )}
                    />
                </div>
                <BrandOwnerCheckbox $checked={isBrandOwner}>
                    <Checkbox
                        checked={isBrandOwner}
                        onChange={(event) => setIsBrandOwner(event.target.checked)}
                    >
                        {t('pages.registries.createBrand.brandOwnerLabel')}
                    </Checkbox>
                </BrandOwnerCheckbox>
            </Fields>
            <WarningBox>
                <WarningTitle>{t('pages.registries.createBrand.warningTitle')}</WarningTitle>
                <p>{t('pages.registries.createBrand.warningAnalytics')}</p>
                <p>{t('pages.registries.createBrand.warningTradeName')}</p>
                <p>{t('pages.registries.createBrand.warningManufacturer')}</p>
            </WarningBox>
            <Footer>
                <Button
                    type="button"
                    variant={ButtonVariant.Outline}
                    disabled={isPending}
                    onClick={onBack}
                >
                    {t('components.form.Cancel')}
                </Button>
                <Button
                    type="button"
                    variant={ButtonVariant.Normal}
                    isLoading={isPending}
                    disabled={!isValid}
                    onClick={onSubmit}
                >
                    {t('pages.registries.createBrand.submit')}
                </Button>
            </Footer>
        </Container>
    );
};
